import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { SidebarItemComponent } from '@shared/components/sidebar-item/sidebar-item.component';
import { SearchBarComponent } from '@shared/components/search-bar/search-bar.component';
import { AuthService } from '@core/services/auth.service';
import { SidebarMenuService } from '@core/services/sidebar-menu.service';
import { APP_ROUTES } from '@core/constants/app-routes.const';
import { TranslocoModule } from '@jsverse/transloco';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [SidebarItemComponent, SearchBarComponent, TranslocoModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit, OnDestroy {
  //Inyección de dependencias
  private authService = inject(AuthService);
  private sidebarMenuService = inject(SidebarMenuService);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  //Constantes

  /**
   * Retardos de intención del hover. Sin ellos el riel se desplegaba con el
   * simple roce del cursor camino al contenido, que es lo que los usuarios
   * reportaban como engorroso.
   *
   * El de cierre es mayor a propósito: es lo que evita el parpadeo cuando el
   * mouse se sale un instante al moverse en diagonal hacia un ítem de abajo.
   */
  private static readonly HOVER_OPEN_DELAY_MS = 250;
  private static readonly HOVER_CLOSE_DELAY_MS = 400;

  readonly routes = APP_ROUTES;

  //Variables
  readonly activeItem = this.sidebarMenuService.activeItem;
  isSidebarOpen: boolean = false;
  isNavBtnSelected: boolean = false;

  readonly isProveedorProducto = computed(() =>
    this.authService.hasRole('PROVEEDOR_PRODUCTO'),
  );

  private openTimer?: ReturnType<typeof setTimeout>;
  private closeTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    effect(() => {
      this.isSidebarOpen = this.sidebarMenuService.isSidebarOpen();
    });
    effect(() => {
      this.isNavBtnSelected = this.sidebarMenuService.isNavBtnSelected();
    });
  }

  ngOnInit(): void {
    this.sidebarMenuService.loadFromLocalStorage();
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  //Métodos

  /**
   * El cursor entró al riel: se agenda la apertura en vez de aplicarla. Si solo
   * estaba de paso, el `mouseleave` cancela el temporizador antes de que corra.
   */
  onPointerEnter(): void {
    // Cancelar aquí un cierre pendiente es lo que hace que salir y volver de
    // inmediato no vuelva a pagar el retardo de apertura: el panel nunca se
    // cerró, así que no hay nada que reabrir.
    this.clearTimers();

    if (this.isSidebarOpen) {
      return;
    }

    this.openTimer = setTimeout(
      () => this.openSidebar(),
      SidebarComponent.HOVER_OPEN_DELAY_MS,
    );
  }

  /** El cursor salió del riel: descarta la apertura pendiente o agenda el cierre. */
  onPointerLeave(): void {
    this.clearTimers();

    // Con el pin activo el riel no se colapsa; `closeSidebar()` lo valida
    // igual, pero así tampoco se deja un temporizador corriendo de más.
    if (!this.isSidebarOpen || this.isNavBtnSelected) {
      return;
    }

    this.closeTimer = setTimeout(
      () => this.closeSidebar(),
      SidebarComponent.HOVER_CLOSE_DELAY_MS,
    );
  }

  /**
   * El foco entró al riel (tabulador). Aquí no va retardo: tabular es una
   * acción deliberada, no un roce accidental, y esperar rompería la navegación
   * por teclado.
   */
  onFocusIn(event: FocusEvent): void {
    this.clearTimers();
    this.openSidebar();
    // Al abrir, la lupa se desmonta y el foco caería en `body` (que además
    // cierra el panel por `focusout`): se pasa al buscador, como con el clic.
    if (
      (event.target as HTMLElement | null)?.closest('.sidebar__search-toggle')
    ) {
      this.focusMenuSearch();
    }
  }

  /**
   * Ojo: `focusout` también dispara al pasar el foco de un ítem del riel al
   * siguiente (sale del viejo antes de entrar al nuevo). El cierre queda
   * agendado y el `onFocusIn()` inmediatamente posterior lo cancela; el margen
   * del retardo de cierre es de sobra para eso, así que no hace falta
   * inspeccionar `relatedTarget`.
   */
  onFocusOut(): void {
    this.onPointerLeave();
  }

  openSidebar(): void {
    this.isSidebarOpen = true;
    this.sidebarMenuService.setSidebarOpen(this.isSidebarOpen);
  }

  closeSidebar(): void {
    if (this.isNavBtnSelected) {
      return;
    }
    this.isSidebarOpen = false;
    this.sidebarMenuService.setSidebarOpen(this.isSidebarOpen);
    // Al colapsar el menú se descarta el filtro para no ocultar ítems
    // mientras el buscador no está visible.
    this.sidebarMenuService.clearMenuFilter();
  }

  /**
   * Lupa del riel colapsado: el foco ya abrió el panel (`onFocusIn`), falta
   * llevarlo al buscador, que se monta en el siguiente ciclo de render.
   */
  onSearchToggleClick(): void {
    this.clearTimers();
    this.openSidebar();
    this.focusMenuSearch();
  }

  private focusMenuSearch(): void {
    setTimeout(() =>
      this.host.nativeElement
        .querySelector<HTMLInputElement>('.sidebar__search input')
        ?.focus(),
    );
  }

  onMenuSearch(value: string): void {
    this.sidebarMenuService.setMenuFilter(value);
  }

  onMenuSearchClear(): void {
    this.sidebarMenuService.clearMenuFilter();
  }

  private clearTimers(): void {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    this.openTimer = undefined;
    this.closeTimer = undefined;
  }
}
